import { useMemo, useRef, useState, useEffect } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useToast } from "@/components/ui/use-toast";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Check,
  ChevronsUpDown,
  Plus,
  X,
  Search,
  Loader2,
  Shield,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  apiService,
  apiRequest,
  getApiErrorMessage,
  getApiErrorStatus,
} from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuthStore } from "@/stores/authStore";
import { CONSOLE_PERMISSIONS } from "@/constants/permissions";

// Type definitions
interface DoseForm {
  dose_form_id: number;
  display_id: string;
  dose_form_name: string;
}

interface MedicineUnit {
  unit_id: number;
  unit_name: string;
  unit_short_code: string;
}

interface MedicineAPI {
  api_id: number;
  api_name: string;
  permitted: boolean;
}

interface MedicineSearchModel {
  brand_id: string;
  brand_name: string;
  product_name: string;
}

interface ActiveIngredient {
  apiId?: number;
  apiName: string;
  potency: string;
  unit: string;
}

const ingredientSchema = z
  .object({
    apiId: z.number().optional(),
    apiName: z.string().min(1, "API name is required"),
    potency: z.string().min(1, "Potency is required"),
    unit: z.string().min(1, "Unit is required"),
  })
  .refine((data) => data.apiId && data.apiId > 0, {
    message: "API selection is required",
    path: ["apiName"],
  });

const medicineSchema = z.object({
  medicineName: z.string().min(1, "Medicine name is required"),
  doseForm: z.string().min(1, "Dose form is required"),
  dispensingUnit: z.string().min(1, "Dispensing unit is required"),
  ingredients: z
    .array(ingredientSchema)
    .min(1, "At least one ingredient is required"),
});

type MedicineFormValues = z.infer<typeof medicineSchema>;

interface ComboboxOption {
  label: string;
  value: string;
}

// API Search Dialog Component
const APISearchDialog = ({
  open,
  onOpenChange,
  onSelect,
  selectedAPIs = [],
  medicineUnits = [],
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (apis: MedicineAPI[]) => void;
  selectedAPIs?: MedicineAPI[];
  medicineUnits: MedicineUnit[];
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<MedicineAPI[]>([]);
  const [selected, setSelected] = useState<MedicineAPI[]>(selectedAPIs);
  const [isSearching, setIsSearching] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [showCreateConfirm, setShowCreateConfirm] = useState(false);
  const { toast } = useToast();
  const prevOpenRef = useRef(open);

  // Reset state ONLY when dialog opens (transitions from closed to open)
  useEffect(() => {
    // Only reset when opening (previous was false, now is true)
    if (open && !prevOpenRef.current) {
      setSelected(selectedAPIs);
      setSearchQuery("");
      setSearchResults([]);
      setHasSearched(false);
      setShowCreateConfirm(false);
    }
    prevOpenRef.current = open;
  }, [open, selectedAPIs]);

  const searchAPIs = async (query: string) => {
    if (query.length < 2) {
      setSearchResults([]);
      setHasSearched(false);
      return;
    }

    setIsSearching(true);
    setHasSearched(true);
    try {
      const response = await apiRequest<{ results: MedicineAPI[] }>(() =>
        apiService.searchAPIs(query)
      );

      // Filter out already selected APIs
      const filteredResults = response.results.filter(
        (api) => !selected.some((sel) => sel.api_id === api.api_id)
      );
      setSearchResults(filteredResults);
    } catch (error) {
      console.error("Error searching APIs:", error);
      setSearchResults([]);
      toast({
        title: "Error",
        description: "Failed to search APIs. Please check your connection.",
        variant: "destructive",
      });
    } finally {
      setIsSearching(false);
    }
  };

  const createNewAPI = async (apiName: string) => {
    setIsCreating(true);
    try {
      const response = await apiRequest<MedicineAPI>(() =>
        apiService.createAPI(apiName)
      );

      toast({
        title: "Success",
        description: `${apiName} was added successfully as a new API. You can now use it.`,
      });

      // Refresh search results
      await searchAPIs(apiName);
      setShowCreateConfirm(false);
    } catch (error: unknown) {
      const isPermissionError = getApiErrorStatus(error) === 403;
      console.error("Error creating API:", error);
      toast({
        title: isPermissionError ? "Access Denied" : "Error",
        description: isPermissionError
          ? "Your permissions no longer allow medicine database changes."
          : getApiErrorMessage(
              error,
              "Failed to create API. Please try again."
            ),
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const toggleAPISelection = (api: MedicineAPI) => {
    const isSelected = selected.some((sel) => sel.api_id === api.api_id);
    if (isSelected) {
      setSelected((prev) => prev.filter((sel) => sel.api_id !== api.api_id));
      // Add back to search results if it matches current search
      if (
        searchQuery.length >= 2 &&
        api.api_name.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !searchResults.some((result) => result.api_id === api.api_id)
      ) {
        setSearchResults((prev) =>
          [...prev, api].sort((a, b) => a.api_name.localeCompare(b.api_name))
        );
      }
    } else {
      setSelected((prev) => [...prev, api]);
      setSearchResults((prev) =>
        prev.filter((result) => result.api_id !== api.api_id)
      );
      // Clear the search field after selection
      setSearchQuery("");
    }
    onSelect(
      isSelected
        ? selected.filter((sel) => sel.api_id !== api.api_id)
        : [...selected, api]
    );
  };

  useEffect(() => {
    const timer = setTimeout(() => searchAPIs(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>Select Active Ingredients</DialogTitle>
            <DialogDescription>
              Search and select active ingredients for your medicine. You can
              select multiple ingredients or create new ones if not found.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Selected APIs */}
            {selected.length > 0 && (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-2">
                  {selected.map((api) => (
                    <Badge
                      key={api.api_id}
                      variant="secondary"
                      className="cursor-pointer hover:bg-destructive hover:text-destructive-foreground"
                      onClick={() => toggleAPISelection(api)}
                    >
                      {api.api_name}
                      <X className="ml-1 h-3 w-3" />
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Search */}
            <div className="space-y-2">
              <label className="text-sm text-muted-foreground">
                Search for active ingredients
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search for API"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Search Results */}
            <div className="min-h-[200px] max-h-[200px] overflow-y-auto border rounded-lg p-4 bg-muted/30">
              {isSearching ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : hasSearched && searchResults.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full space-y-4">
                  <p className="text-muted-foreground">
                    No results found. Do you want to add new?
                  </p>
                  <Button
                    onClick={() => setShowCreateConfirm(true)}
                    disabled={isCreating}
                    size="sm"
                  >
                    {isCreating ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Plus className="mr-2 h-4 w-4" />
                    )}
                    Add New
                  </Button>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {searchResults.map((api) => (
                    <Badge
                      key={api.api_id}
                      variant="outline"
                      className="cursor-pointer hover:bg-primary hover:text-primary-foreground"
                      onClick={() => toggleAPISelection(api)}
                    >
                      {api.api_name}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create API Confirmation Dialog */}
      <AlertDialog open={showCreateConfirm} onOpenChange={setShowCreateConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will add <strong>{searchQuery}</strong> as a new API in the
              database. Please confirm
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Go Back</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => createNewAPI(searchQuery)}
              disabled={isCreating}
            >
              {isCreating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

const Combobox = ({
  value,
  options,
  onChange,
  placeholder,
  searchPlaceholder,
  className,
}: {
  value?: string;
  options: ComboboxOption[];
  onChange: (value: string) => void;
  placeholder: string;
  searchPlaceholder?: string;
  className?: string;
}) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [contentWidth, setContentWidth] = useState<number | undefined>(
    undefined
  );

  useEffect(() => {
    if (open && triggerRef.current) {
      setContentWidth(triggerRef.current.getBoundingClientRect().width);
    }
  }, [open]);

  const selected = options.find((o) => o.value === value)?.label;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between overflow-hidden min-w-0",
            className
          )}
          ref={triggerRef}
        >
          <span className="truncate whitespace-nowrap max-w-full">
            {selected || placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="start"
        className="p-0"
        style={{ width: contentWidth }}
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder || "Search..."} />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              {options.map((o) => (
                <CommandItem
                  key={o.value}
                  value={o.label}
                  onSelect={() => {
                    onChange(o.value);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      o.value === value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {o.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export const AddMedicineForm = () => {
  const { toast } = useToast();
  const { hasPermission } = useAuthStore();
  const [reviewOpen, setReviewOpen] = useState(false);
  const [apiSearchOpen, setApiSearchOpen] = useState(false);
  const [selectedIngredientIndex, setSelectedIngredientIndex] =
    useState<number>(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);

  // API data states
  const [doseForms, setDoseForms] = useState<DoseForm[]>([]);
  const [medicineUnits, setMedicineUnits] = useState<MedicineUnit[]>([]);
  const [isLoadingDoseForms, setIsLoadingDoseForms] = useState(false);
  const [isLoadingUnits, setIsLoadingUnits] = useState(false);

  // Medicine search states
  const [medicineSearchResults, setMedicineSearchResults] = useState<
    MedicineSearchModel[]
  >([]);
  const [isSearchingMedicines, setIsSearchingMedicines] = useState(false);
  const [showMedicineDropdown, setShowMedicineDropdown] = useState(false);
  const [hasSearchedMedicines, setHasSearchedMedicines] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(
    null
  );

  const defaultValues: MedicineFormValues = useMemo(
    () => ({
      medicineName: "",
      doseForm: "",
      dispensingUnit: "",
      ingredients: [{ apiId: 0, apiName: "", potency: "", unit: "" }],
    }),
    []
  );

  const form = useForm<MedicineFormValues>({
    resolver: zodResolver(medicineSchema),
    defaultValues,
    mode: "onSubmit",
  });

  const { fields, append, remove, update } = useFieldArray({
    control: form.control,
    name: "ingredients",
  });

  // Fetch dose forms on component mount
  useEffect(() => {
    const fetchDoseForms = async () => {
      setIsLoadingDoseForms(true);
      try {
        const response = await apiRequest<DoseForm[]>(() =>
          apiService.getDoseForms()
        );
        setDoseForms(response);
      } catch (error) {
        console.error("Error fetching dose forms:", error);
        setDoseForms([]); // Keep empty to show "Data won't load"
        toast({
          title: "Error",
          description:
            "Failed to load dose forms. Please check your connection.",
          variant: "destructive",
        });
      } finally {
        setIsLoadingDoseForms(false);
      }
    };

    const fetchMedicineUnits = async () => {
      setIsLoadingUnits(true);
      try {
        const response = await apiRequest<MedicineUnit[]>(() =>
          apiService.getMedicineUnits()
        );
        setMedicineUnits(response);
      } catch (error) {
        console.error("Error fetching medicine units:", error);
        setMedicineUnits([]); // Keep empty to show "Data won't load"
        toast({
          title: "Error",
          description:
            "Failed to load medicine units. Please check your connection.",
          variant: "destructive",
        });
      } finally {
        setIsLoadingUnits(false);
      }
    };

    fetchDoseForms();
    fetchMedicineUnits();
  }, [toast]);

  // Medicine search function
  const searchExistingMedicines = async (query: string) => {
    if (query.length < 2) {
      setMedicineSearchResults([]);
      setShowMedicineDropdown(false);
      setHasSearchedMedicines(false);
      return;
    }

    setIsSearchingMedicines(true);
    setHasSearchedMedicines(true);
    try {
      const response = await apiRequest<{ results: MedicineSearchModel[] }>(
        () => apiService.searchMedicines(query, form.getValues("doseForm"))
      );

      setMedicineSearchResults(response.results || []);
      setShowMedicineDropdown(true);
    } catch (error) {
      console.error("Error searching medicines:", error);
      setMedicineSearchResults([]);
      setShowMedicineDropdown(true); // Show dropdown even on error to show "create new" option
    } finally {
      setIsSearchingMedicines(false);
    }
  };

  const handleMedicineNameChange = (value: string) => {
    form.setValue("medicineName", value);

    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Set new timeout for debounced search
    const timeout = setTimeout(() => {
      searchExistingMedicines(value);
    }, 500); // 500ms debounce like Flutter

    setSearchTimeout(timeout);
  };

  const handleMedicineSelect = (medicine: MedicineSearchModel) => {
    form.setValue("medicineName", medicine.brand_name);
    setShowMedicineDropdown(false);
    setMedicineSearchResults([]);
  };

  const handleCreateNewMedicine = () => {
    // Keep the current text in the field and close dropdown
    setShowMedicineDropdown(false);
    setMedicineSearchResults([]);
  };

  const handleSubmit = (values: MedicineFormValues) => {
    setReviewOpen(true);
  };

  const handleAPISelect = (apis: MedicineAPI[]) => {
    const currentIngredients = form.getValues("ingredients");

    if (apis.length === 0) {
      // Clear current ingredient
      update(selectedIngredientIndex, {
        apiId: 0,
        apiName: "",
        potency: currentIngredients[selectedIngredientIndex]?.potency || "",
        unit: currentIngredients[selectedIngredientIndex]?.unit || "",
      });
    } else if (apis.length === 1) {
      // Update current ingredient
      const api = apis[0];
      update(selectedIngredientIndex, {
        apiId: api.api_id,
        apiName: api.api_name,
        potency: currentIngredients[selectedIngredientIndex]?.potency || "",
        unit: currentIngredients[selectedIngredientIndex]?.unit || "",
      });
    } else {
      // Multiple selection - update current and add new ones
      const firstAPI = apis[0];
      update(selectedIngredientIndex, {
        apiId: firstAPI.api_id,
        apiName: firstAPI.api_name,
        potency: currentIngredients[selectedIngredientIndex]?.potency || "",
        unit: currentIngredients[selectedIngredientIndex]?.unit || "",
      });

      // Add new ingredients for remaining APIs
      for (let i = 1; i < apis.length; i++) {
        const api = apis[i];
        append({
          apiId: api.api_id,
          apiName: api.api_name,
          potency: "",
          unit: "",
        });
      }
    }
  };

  const handleConfirmSubmit = async () => {
    setIsSubmitting(true);
    try {
      const values = form.getValues();

      // Prepare API payload
      const payload = {
        name: values.medicineName,
        medicine_unit: values.dispensingUnit.toLowerCase(),
        dose_form: values.doseForm,
        api: values.ingredients.map((ing) => ({
          id: ing.apiId,
          name: ing.apiName,
          potency: ing.potency,
          unit: ing.unit.toLowerCase(),
        })),
      };

      await apiRequest(() => apiService.addMedicine(payload));

      setReviewOpen(false);
      setSuccessOpen(true);
    } catch (error: unknown) {
      const isPermissionError = getApiErrorStatus(error) === 403;
      console.error("Error adding medicine:", error);
      toast({
        title: isPermissionError ? "Access Denied" : "Error",
        description: isPermissionError
          ? "Your permissions no longer allow medicine database changes."
          : getApiErrorMessage(
              error,
              "Failed to add medicine. Please try again."
            ),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSuccessOk = () => {
    setSuccessOpen(false);

    // Reset form completely
    form.reset({
      medicineName: "",
      doseForm: "",
      dispensingUnit: "",
      ingredients: [{ apiId: 0, apiName: "", potency: "", unit: "" }],
    });

    // Clear all search states
    setMedicineSearchResults([]);
    setShowMedicineDropdown(false);
    setHasSearchedMedicines(false);
    if (searchTimeout) {
      clearTimeout(searchTimeout);
      setSearchTimeout(null);
    }

    toast({
      title: "Success",
      description: "Medicine added successfully!",
    });
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  if (!hasPermission(CONSOLE_PERMISSIONS.MANAGE_MEDICINE_DB)) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-destructive" />
            Access Denied
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <Shield className="h-16 w-16 text-destructive mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Permission Required
            </h3>
            <p className="text-muted-foreground mb-4">
              You need medicine-database management permission to access this
              feature.
            </p>
            <p className="text-sm text-muted-foreground">
              Please contact your administrator if you believe this is an error.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="h-full flex flex-col">
        <CardHeader className="shrink-0">
          <CardTitle>Add New Medicine</CardTitle>
        </CardHeader>
        <CardContent className="flex-1 overflow-y-auto">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleSubmit)}
              className="space-y-6 max-w-5xl mx-auto"
            >
              {/* Medicine Name */}
              <FormField
                control={form.control}
                name="medicineName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Medicine Name *</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          placeholder="Enter Medicine Name"
                          value={field.value}
                          onChange={(e) =>
                            handleMedicineNameChange(e.target.value)
                          }
                          onFocus={() => {
                            if (field.value.length >= 2) {
                              setShowMedicineDropdown(true);
                            }
                          }}
                          onBlur={() => {
                            // Delay hiding dropdown to allow clicks
                            setTimeout(
                              () => setShowMedicineDropdown(false),
                              200
                            );
                          }}
                        />

                        {/* Medicine Search Dropdown */}
                        {showMedicineDropdown && (
                          <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-card border border-border rounded-md shadow-lg max-h-60 overflow-y-auto">
                            {isSearchingMedicines ? (
                              <div className="flex items-center justify-center p-4">
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                <span className="text-sm text-muted-foreground">
                                  Searching...
                                </span>
                              </div>
                            ) : hasSearchedMedicines &&
                              medicineSearchResults.length === 0 ? (
                              <div className="p-4">
                                <div className="text-sm text-muted-foreground mb-2">
                                  No existing medicine found for "{field.value}"
                                </div>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={handleCreateNewMedicine}
                                  className="w-full"
                                >
                                  <Plus className="mr-2 h-4 w-4" />
                                  Create New Medicine
                                </Button>
                              </div>
                            ) : medicineSearchResults.length > 0 ? (
                              <div className="py-1">
                                {medicineSearchResults.map(
                                  (medicine, index) => (
                                    <button
                                      key={medicine.brand_id}
                                      type="button"
                                      className="w-full px-4 py-2 text-left text-sm hover:bg-muted focus:bg-muted focus:outline-none"
                                      onClick={() =>
                                        handleMedicineSelect(medicine)
                                      }
                                    >
                                      <div className="font-medium">
                                        {medicine.brand_name}
                                      </div>
                                      <div className="text-xs text-muted-foreground">
                                        {medicine.product_name}
                                      </div>
                                    </button>
                                  )
                                )}
                              </div>
                            ) : null}
                          </div>
                        )}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="border-t pt-6" />

              {/* Dose Form and Dispensing Unit */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="doseForm"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Dose Form *</FormLabel>
                      <FormControl>
                        {isLoadingDoseForms ? (
                          <div className="flex items-center justify-center h-10 border rounded-md">
                            <Loader2 className="h-4 w-4 animate-spin" />
                          </div>
                        ) : doseForms.length === 0 ? (
                          <div className="flex items-center justify-center h-10 border rounded-md bg-muted text-muted-foreground">
                            Data won't load
                          </div>
                        ) : (
                          <Combobox
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="Select Dose Form"
                            searchPlaceholder="Search dose forms"
                            options={doseForms.map((df) => ({
                              label: df.dose_form_name,
                              value: df.dose_form_name,
                            }))}
                          />
                        )}
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="dispensingUnit"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Dispensing Unit *</FormLabel>
                      <FormControl>
                        {isLoadingUnits ? (
                          <div className="flex items-center justify-center h-10 border rounded-md">
                            <Loader2 className="h-4 w-4 animate-spin" />
                          </div>
                        ) : medicineUnits.length === 0 ? (
                          <div className="flex items-center justify-center h-10 border rounded-md bg-muted text-muted-foreground">
                            Data won't load
                          </div>
                        ) : (
                          <Combobox
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="Select Dispensing Unit"
                            searchPlaceholder="Search units"
                            options={medicineUnits.map((mu) => ({
                              label: mu.unit_name,
                              value: mu.unit_short_code,
                            }))}
                          />
                        )}
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Active Ingredients */}
              <div className="space-y-3">
                <div>
                  <h3 className="text-lg font-medium">
                    Select Active Ingredient/s *
                  </h3>
                </div>
                <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
                  {/* Headers */}
                  <div className="grid grid-cols-12 items-center gap-2 px-4 py-3 text-xs text-muted-foreground border-b">
                    <div className="col-span-1">&nbsp;</div>
                    <div className="col-span-4">API Name</div>
                    <div className="col-span-3">API Potency</div>
                    <div className="col-span-3">API Unit</div>
                    <div className="col-span-1 text-right">&nbsp;</div>
                  </div>

                  {/* Ingredient rows */}
                  <div className="max-h-60 overflow-y-auto">
                    <div className="p-2 space-y-2">
                      {fields.map((fieldItem, index) => (
                        <div
                          key={fieldItem.id}
                          className="grid grid-cols-12 items-center gap-2"
                        >
                          <div className="col-span-1 text-xs font-semibold pl-2">
                            {index + 1}.
                          </div>

                          {/* API Name - Searchable with dialog */}
                          <div className="col-span-4">
                            <FormField
                              control={form.control}
                              name={`ingredients.${index}.apiName`}
                              render={({ field }) => (
                                <FormItem>
                                  <FormControl>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      className={cn(
                                        "w-full justify-start text-left font-normal text-xs",
                                        !field.value && "text-muted-foreground",
                                        form.formState.errors.ingredients?.[
                                          index
                                        ]?.apiName && "border-destructive"
                                      )}
                                      onClick={() => {
                                        setSelectedIngredientIndex(index);
                                        setApiSearchOpen(true);
                                      }}
                                    >
                                      <Search className="mr-1 h-3 w-3 shrink-0" />
                                      {field.value || "Search API"}
                                    </Button>
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>

                          {/* API Potency */}
                          <div className="col-span-3">
                            <FormField
                              control={form.control}
                              name={`ingredients.${index}.potency`}
                              render={({ field }) => (
                                <FormItem>
                                  <FormControl>
                                    <Input
                                      placeholder="Potency"
                                      className="text-xs"
                                      {...field}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>

                          {/* API Unit */}
                          <div className="col-span-3">
                            <FormField
                              control={form.control}
                              name={`ingredients.${index}.unit`}
                              render={({ field }) => (
                                <FormItem>
                                  <FormControl>
                                    {medicineUnits.length === 0 ? (
                                      <div className="flex items-center justify-center h-10 border rounded-md bg-muted text-muted-foreground text-xs">
                                        Data won't load
                                      </div>
                                    ) : (
                                      <Combobox
                                        value={field.value}
                                        onChange={field.onChange}
                                        placeholder="Select Unit"
                                        searchPlaceholder="Search units"
                                        className="text-xs"
                                        options={medicineUnits.map((unit) => ({
                                          label: unit.unit_short_code,
                                          value: unit.unit_short_code,
                                        }))}
                                      />
                                    )}
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>

                          {/* Remove button */}
                          <div className="col-span-1 flex justify-end">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                              onClick={() => {
                                if (fields.length > 1) {
                                  remove(index);
                                }
                              }}
                              disabled={fields.length === 1}
                              aria-label="Remove ingredient"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Add button */}
                  <div className="flex justify-end p-2 border-t">
                    <Button
                      type="button"
                      size="sm"
                      onClick={() =>
                        append({ apiId: 0, apiName: "", potency: "", unit: "" })
                      }
                    >
                      <Plus className="mr-2 h-4 w-4" /> Add
                    </Button>
                  </div>
                </div>
              </div>

              {/* Submit button */}
              <div className="pt-2 flex justify-end">
                <Button type="submit">Submit</Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>

      {/* API Search Dialog */}
      <APISearchDialog
        open={apiSearchOpen}
        onOpenChange={setApiSearchOpen}
        onSelect={handleAPISelect}
        selectedAPIs={
          fields[selectedIngredientIndex]
            ? [
                {
                  api_id: fields[selectedIngredientIndex].apiId || 0,
                  api_name: fields[selectedIngredientIndex].apiName || "",
                  permitted: true,
                },
              ].filter((api) => api.api_id > 0)
            : []
        }
        medicineUnits={medicineUnits}
      />

      {/* Confirmation Dialog */}
      <AlertDialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm New Medicine Entry</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-foreground">
                <div>
                  <span className="font-semibold">
                    {form.getValues("medicineName")}
                  </span>
                  {form.getValues("doseForm") && (
                    <span className="text-muted-foreground">
                      {" "}
                      ({form.getValues("doseForm")})
                    </span>
                  )}
                </div>
                <div className="text-sm font-medium text-muted-foreground">
                  Composition:
                </div>
                <div className="space-y-1">
                  {form
                    .getValues("ingredients")
                    .filter((ing) => ing.apiName && ing.potency && ing.unit)
                    .map((ing, idx) => (
                      <div key={idx} className="text-sm">
                        {idx + 1}. {ing.apiName} - {ing.potency} {ing.unit}
                      </div>
                    ))}
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => setReviewOpen(false)}
              disabled={isSubmitting}
            >
              Edit Details
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Confirm & Save
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Success Dialog */}
      <AlertDialog open={successOpen} onOpenChange={setSuccessOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-success">
              Success
            </AlertDialogTitle>
            <AlertDialogDescription>
              Medicine Added successfully!
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={handleSuccessOk}>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default AddMedicineForm;
